import { type ReactElement, useContext } from 'react'

import { Link as DefaultLink } from '@twistopayments/design-system/components/atoms/link'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@twistopayments/design-system/icons/inline'
import { useAtomValue } from 'jotai'

import type { LinkUrl, LinksLinkComponent } from '@twisto/api-cms'

import { AccessibleDiv } from 'homepage/components/atoms/accessible-div'
import { Hidden } from 'homepage/components/atoms/hidden'
import { navigationThemeAtom } from 'homepage/components/navigation/navigation-theme'
import { NestedLinksContext } from 'homepage/components/navigation/nested-links-provider'
import { getLinkUrl } from 'homepage/utils'

import * as styles from './dropdown-link-holder.styles'

type DropdownLinkHolderProps = {
  children: ReactElement | null
  link?: LinksLinkComponent
  fullDropdown?: boolean
}

export const DropdownLinkHolder = ({
  fullDropdown,
  link,
  children,
}: DropdownLinkHolderProps) => {
  const { openList, currentActiveListId } = useContext(NestedLinksContext) ?? {}
  const navigationTheme = useAtomValue(navigationThemeAtom)

  if (!link) {
    return null
  }

  const {
    id,
    color,
    fontWeight,
    hover,
    newTab,
    text,
    underline,
    url,
    variant,
  } = link

  const handleClick = () => {
    if (openList && id) {
      openList(id)
    }
  }

  if (currentActiveListId && currentActiveListId === id) {
    return children
  }

  return (
    <AccessibleDiv
      as="li"
      css={[
        styles.container,
        currentActiveListId && currentActiveListId !== id
          ? styles.hidden
          : undefined,
      ]}
      role="button"
      onActivate={handleClick}
    >
      <Hidden
        as="span"
        breakpoint="lgUp"
        css={[styles.link, fullDropdown && styles.full]}
      >
        <Typography component="span">{text}</Typography>

        <ChevronRightIcon css={styles.icon} size="20" />
      </Hidden>

      <Hidden
        as="div"
        breakpoint="mdDown"
        css={[
          styles.link,
          fullDropdown && styles.full,
          navigationTheme === 'dark' && styles.inverted,
        ]}
        role="link"
      >
        <DefaultLink
          color="light"
          hover={hover}
          href={url}
          target={newTab === true ? '_blank' : undefined}
          to={getLinkUrl({ url } as LinkUrl)}
          underline={underline}
          variant={variant}
        >
          <Typography
            color={color || 'primary'}
            component="span"
            fontWeight={fontWeight}
          >
            {text}
          </Typography>
          <ChevronDownIcon css={styles.icon} size="18" />
        </DefaultLink>

        {children}
      </Hidden>
    </AccessibleDiv>
  )
}
